import type { CharacterGridSpec } from "@/character/grid";
import {
  sheetLayoutFromGrid,
  startRowLoopAtHeight,
} from "@/character/sheet-preview-canvas";
import {
  PLAYER_DISPLAY_HEIGHT_PX,
  SKILL_BUFF_FX_ALPHA,
  SKILL_DEFAULT_COOLDOWN_MS,
  formatSkillCooldownSeconds,
  SKILL_DISPLAY_HEIGHT_PX,
  SKILL_TEMPLATE_FRAME_HEIGHT,
  SKILL_TEMPLATE_FRAME_WIDTH,
} from "@/character/skills/constants";
import type { SkillKind } from "@/character/skills/types";
import { applyAutoBackgroundRemovalFromDataUrl } from "@/home/remove-white-bg";

export type SkillUsageDemoState = {
  name: string;
  kind: SkillKind;
  iconDataUrl: string | null;
  spritesheetDataUrl: string | null;
  grid: CharacterGridSpec;
  durationSec: number;
};

/** Same target heights as HomeRoomScene; compact = 0.75 for narrow modal column. */
function demoDisplayHeights(compact: boolean): { playerH: number; skillH: number } {
  const scale = compact ? 0.75 : 1;
  return {
    playerH: Math.round(PLAYER_DISPLAY_HEIGHT_PX * scale),
    skillH: Math.round(SKILL_DISPLAY_HEIGHT_PX * scale),
  };
}

function demoStageWidth(playerH: number, skillH: number): number {
  const skillW = Math.round((SKILL_TEMPLATE_FRAME_WIDTH / SKILL_TEMPLATE_FRAME_HEIGHT) * skillH);
  const playerW = playerH;
  return Math.max(playerW, skillW);
}

export type SkillUsageDemoMountOptions = {
  /** In modal: stage only — hotbar is the main panel above. */
  compact?: boolean;
  autoPlay?: boolean;
  cooldownMs?: number;
};

export type SkillUsageDemoActor = {
  spritesheetDataUrl: string;
  grid: CharacterGridSpec;
};

export function mountSkillUsageDemo(
  host: HTMLElement,
  actor: SkillUsageDemoActor,
  state: SkillUsageDemoState,
  mountOptions: SkillUsageDemoMountOptions = {},
): () => void {
  const compact = mountOptions.compact ?? false;
  const autoPlay = mountOptions.autoPlay ?? false;
  const cooldownMs = mountOptions.cooldownMs ?? SKILL_DEFAULT_COOLDOWN_MS;
  const { playerH, skillH } = demoDisplayHeights(compact);
  const stageW = demoStageWidth(playerH, skillH);
  const cleanups: Array<() => void> = [];
  let fxCleanup: (() => void) | null = null;
  let buffTimer: ReturnType<typeof setTimeout> | null = null;

  const cdSec = formatSkillCooldownSeconds(cooldownMs);
  const kindLabel = state.kind === "buff" ? "บัฟ" : "โจมตี";
  const hasSkill = Boolean(state.spritesheetDataUrl && state.grid);

  const extras = compact
    ? ""
    : `
      <p class="hint skill-demo-hint">กดช่องสกิลด้านบนเพื่อดูตัวอย่าง</p>`;

  host.innerHTML = `
    <div class="skill-game-demo${compact ? " skill-game-demo--compact" : ""}">
      <div class="skill-game-demo-stage" aria-hidden="true">
        <div
          class="skill-game-demo-actors"
          style="--demo-stage-w: ${stageW}px; --demo-stage-h: ${skillH}px; --demo-player-h: ${playerH}px; --demo-skill-h: ${skillH}px"
        >
          <div class="skill-game-demo-floor" aria-hidden="true"></div>
          <div class="skill-game-demo-stack">
            <canvas class="skill-game-demo-char"></canvas>
            <canvas class="skill-game-demo-fx"></canvas>
          </div>
        </div>
      </div>
      <p class="skill-game-demo-caption" id="skill-demo-caption">
        ${hasSkill ? `${kindLabel} · คูลดาวน์ ${cdSec}วิ` : "อัปโหลดและครอบเฟรมเพื่อดูเอฟเฟกต์"}
      </p>
      ${extras}
    </div>
  `;

  const charCanvas = host.querySelector<HTMLCanvasElement>(".skill-game-demo-char");
  const fxCanvas = host.querySelector<HTMLCanvasElement>(".skill-game-demo-fx");
  const captionEl = host.querySelector<HTMLElement>("#skill-demo-caption");
  const charImg = new Image();
  charImg.decoding = "async";
  charImg.onload = () => {
    if (!charCanvas) return;
    const layout = sheetLayoutFromGrid(charImg, actor.grid);
    cleanups.push(startRowLoopAtHeight(charCanvas, charImg, layout, 0, playerH, 8, "loop"));
  };
  charImg.src = actor.spritesheetDataUrl;

  const clearFx = () => {
    fxCleanup?.();
    fxCleanup = null;
    if (buffTimer) {
      clearTimeout(buffTimer);
      buffTimer = null;
    }
    const ctx = fxCanvas?.getContext("2d");
    if (ctx && fxCanvas) {
      ctx.clearRect(0, 0, fxCanvas.width, fxCanvas.height);
    }
    if (fxCanvas) fxCanvas.style.opacity = "1";
  };

  const playDemo = () => {
    if (!hasSkill || !fxCanvas || !state.spritesheetDataUrl) return;
    clearFx();
    void (async () => {
      const sheetUrl = await applyAutoBackgroundRemovalFromDataUrl(state.spritesheetDataUrl!);
      const skillImg = new Image();
      await new Promise<void>((resolve, reject) => {
        skillImg.onload = () => resolve();
        skillImg.onerror = () => reject(new Error("โหลดสกิลไม่สำเร็จ"));
        skillImg.src = sheetUrl;
      });
      const layout = sheetLayoutFromGrid(skillImg, state.grid);
      const fps = 14;
      if (state.kind === "buff") {
        fxCanvas.style.opacity = String(SKILL_BUFF_FX_ALPHA);
        fxCleanup = startRowLoopAtHeight(fxCanvas, skillImg, layout, 0, skillH, fps, "loop");
        const ms = Math.min(60_000, Math.max(1000, state.durationSec * 1000));
        if (captionEl) {
          captionEl.textContent = `บัฟ ${state.name || "สกิล"} · ${Math.round(ms / 1000)} วิ`;
        }
        buffTimer = setTimeout(() => {
          clearFx();
          if (captionEl) captionEl.textContent = `บัฟจบ · คูลดาวน์ ${cdSec} วิ`;
        }, Math.min(ms, 4000));
      } else {
        fxCanvas.style.opacity = "1";
        if (captionEl) {
          captionEl.textContent = `โจมตี · ${state.name || "สกิล"}`;
        }
        fxCleanup = startRowLoopAtHeight(fxCanvas, skillImg, layout, 0, skillH, fps, "once");
        setTimeout(() => {
          if (captionEl) captionEl.textContent = `โจมตีจบ · คูลดาวน์ ${cdSec} วิ`;
        }, 600);
      }
    })().catch(() => {
      if (captionEl) captionEl.textContent = "โหลดตัวอย่างสกิลไม่สำเร็จ";
    });
  };

  if (autoPlay && hasSkill) {
    requestAnimationFrame(() => playDemo());
  }

  return () => {
    clearFx();
    for (const stop of cleanups) stop();
    host.replaceChildren();
  };
}
